import React, { useCallback, useEffect, useState } from 'react';
import {
  Loader2, Music2, PlugZap, Send, ShieldCheck, MessageSquareOff,
  Image as ImageIcon, CheckCircle2, Clock, XCircle,
} from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const STATUS_UI = {
  simulated: { label: 'Sandbox', Icon: PlugZap, cls: 'text-violet-600' },
  processing: { label: 'Procesando', Icon: Clock, cls: 'text-amber-600' },
  success: { label: 'Publicado', Icon: CheckCircle2, cls: 'text-green-600' },
  failed: { label: 'Falló', Icon: XCircle, cls: 'text-red-600' },
};

/**
 * Composer de TikTok dentro de la vista previa (desacoplado de Meta).
 * Título/descripción editables, privacidad SIN default, comentarios, modo y
 * consentimiento legal obligatorio. Sandbox/dry-run.
 */
export default function TikTokComposer({ propertyId }) {
  const { toast } = useToast();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [privacy, setPrivacy] = useState('');          // sin valor por defecto
  const [disableComment, setDisableComment] = useState(false);
  const [postMode, setPostMode] = useState('MEDIA_UPLOAD');
  const [consent, setConsent] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    // creator-info y caption son INDEPENDIENTES: si creator-info falla (p.ej. el
    // scope video.publish no está en MEDIA_UPLOAD), el copy debe cargarse igual.
    const [ciRes, capRes] = await Promise.allSettled([
      api.tiktokCreatorInfo(),
      api.tiktokCaption(propertyId),
    ]);
    if (ciRes.status === 'fulfilled') {
      setInfo(ciRes.value);
      if (ciRes.value.comment_disabled) setDisableComment(true);
    } else {
      setInfo({ connected: false, creator_info_available: false, privacy_level_options: [] });
    }
    if (capRes.status === 'fulfilled') {
      setTitle(capRes.value.title || '');
      setDescription(capRes.value.description || '');
    } else {
      toast({ title: 'No se pudo generar el copy', description: String(capRes.reason?.message || capRes.reason), variant: 'destructive' });
    }
    setLoading(false);
  }, [propertyId, toast]);

  useEffect(() => { load(); }, [load]);

  const publish = async () => {
    if (postMode === 'DIRECT_POST' && !privacy) { toast({ title: 'Selecciona la privacidad (requerida para DIRECT_POST)', variant: 'destructive' }); return; }
    if (!consent) { toast({ title: 'Debes aceptar el consentimiento de TikTok', variant: 'destructive' }); return; }
    setPublishing(true);
    try {
      const res = await api.tiktokPublish(propertyId, {
        title, description, privacy_level: privacy,
        disable_comment: disableComment, post_mode: postMode, consent,
      });
      setResult(res);
      toast({ title: 'Enviado a TikTok', description: `Sandbox · ${res.post_mode} · id ${res.publish_id?.slice(0, 14)}…` });
    } catch (e) {
      toast({ title: 'Error al publicar', description: String(e.message || e), variant: 'destructive' });
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return <div className="rounded-xl border border-gray-200 p-4 flex items-center gap-2 text-sm text-gray-500" data-testid="tiktok-composer-loading"><Loader2 className="w-4 h-4 animate-spin" /> Cargando TikTok…</div>;
  }

  const St = result ? (STATUS_UI[result.status] || STATUS_UI.processing) : null;

  return (
    <section className="rounded-xl border border-gray-200 p-4 bg-gray-50/60" data-testid="tiktok-composer">
      <div className="flex items-center gap-2 mb-3">
        <Music2 className="w-4 h-4 text-black" />
        <h4 className="font-semibold text-habita-dark text-sm">TikTok — Photo Post (carrusel)</h4>
        <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-bold">Sandbox</span>
      </div>

      {!info?.connected && (
        <p className="text-xs text-amber-700 mb-3">TikTok no está conectado — se simulará en sandbox. Conéctalo en la configuración para publicar de verdad.</p>
      )}

      <div className="space-y-3">
        <div>
          <label className="text-xs font-semibold text-gray-600">Título</label>
          <input data-testid="tiktok-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={90}
            className="w-full px-3 py-2 rounded-xl border border-gray-200 outline-none focus:border-habita text-sm" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-600">Descripción</label>
          <textarea data-testid="tiktok-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={4}
            className="w-full px-3 py-2 rounded-xl border border-gray-200 outline-none focus:border-habita text-sm" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-gray-600 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Privacidad {postMode === 'DIRECT_POST' ? '(obligatorio)' : '(opcional)'}</label>
            <select data-testid="tiktok-privacy" value={privacy} onChange={(e) => setPrivacy(e.target.value)}
              disabled={postMode === 'MEDIA_UPLOAD'}
              className="w-full px-3 h-10 rounded-xl border border-gray-200 outline-none focus:border-habita text-sm bg-white disabled:bg-gray-100 disabled:text-gray-400">
              <option value="">— Selecciona —</option>
              {(info?.privacy_level_options || []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            {postMode === 'MEDIA_UPLOAD' && (
              <p className="text-[11px] text-gray-500 mt-1">En MEDIA_UPLOAD elegirás la privacidad en la app de TikTok al finalizar el borrador.</p>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> Modo</label>
            <select data-testid="tiktok-postmode" value={postMode} onChange={(e) => setPostMode(e.target.value)}
              className="w-full px-3 h-10 rounded-xl border border-gray-200 outline-none focus:border-habita text-sm bg-white">
              <option value="MEDIA_UPLOAD">MEDIA_UPLOAD (revisión en la app)</option>
              <option value="DIRECT_POST">DIRECT_POST (scaffold)</option>
            </select>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-600" data-testid="tiktok-disable-comment">
          <input type="checkbox" checked={disableComment} onChange={(e) => setDisableComment(e.target.checked)}
            disabled={info?.comment_disabled} />
          <MessageSquareOff className="w-3.5 h-3.5" /> Desactivar comentarios
        </label>

        <label className="flex items-start gap-2 text-xs text-gray-600 bg-white rounded-lg border border-gray-200 p-2.5" data-testid="tiktok-consent">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
          <span>{info?.consent_text}</span>
        </label>

        <button onClick={publish} disabled={publishing} data-testid="tiktok-publish-btn"
          className="px-5 h-10 rounded-xl bg-black text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
          {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar a TikTok
        </button>

        {result && St && (
          <div className="flex items-center gap-2 text-sm mt-1" data-testid="tiktok-result">
            <St.Icon className={`w-4 h-4 ${St.cls}`} />
            <span className={St.cls}>{St.label}</span>
            <span className="text-gray-400">· {result.post_mode} · {result.images?.length} imagen(es) · id {result.publish_id?.slice(0, 16)}…</span>
          </div>
        )}
      </div>
    </section>
  );
}
